// Staff "Partner Brands" list — the white-label skin records behind the public
// prequalification funnel (/prequalify/{slug}). Gated by
// Permissions.Partners.ManageBrands (route-level in App.tsx).

import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import type { PartnerBrandSummary } from "@/api/partnerBrands";
import { usePartnerBrands } from "@/hooks/partners/usePartnerBrands";
import { Badge, Button, useDocumentTitle } from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";

/**
 * The Owner column. Ownership shipped with the ownership PR but nothing in the
 * product displayed it, so this is the first surface where a staff member can
 * see which prequalification site belongs to whom.
 *
 * Three states, and the distinction between the last two matters:
 *  - owned  → the owner's name, linking to their record;
 *  - UNOWNED (no owner id at all) → a supported state for a general referral
 *    partner, so it reads plainly and offers the attach-owner action;
 *  - owner id set but unresolvable (soft-deleted record) → "Owner unavailable",
 *    never silently collapsed into "Unowned", which would misreport the row.
 *
 * The row itself already navigates to the brand, so the owner links stop
 * propagation to avoid a double-navigation to a different target.
 */
function OwnerCell({ brand }: { brand: PartnerBrandSummary }) {
  const stop = (ev: React.MouseEvent) => ev.stopPropagation();

  if (brand.ownerBrokerId) {
    return brand.ownerName ? (
      <Link
        to={`/brokers/${brand.ownerBrokerId}`}
        onClick={stop}
        className="text-gold-shadow hover:underline"
      >
        {brand.ownerName}
      </Link>
    ) : (
      <span className="text-gray-mid italic">Owner unavailable</span>
    );
  }

  if (brand.ownerBrokerageId) {
    return brand.ownerName ? (
      <Link
        to={`/brokerages/${brand.ownerBrokerageId}`}
        onClick={stop}
        className="text-gold-shadow hover:underline"
      >
        {brand.ownerName}
      </Link>
    ) : (
      <span className="text-gray-mid italic">Owner unavailable</span>
    );
  }

  // Ownerless. The attach-owner action lives on the brand's own page (the
  // Ownership section) — this is simply the place staff now reach it from.
  return (
    <span className="inline-flex items-center gap-space-2">
      <span className="text-gray-mid">Unowned</span>
      <Link
        to={`/partners/brands/${brand.id}`}
        onClick={stop}
        className="text-small text-gold-shadow hover:underline"
      >
        Attach owner
      </Link>
    </span>
  );
}

export function PartnerBrandsListPage() {
  useDocumentTitle("Partner Brands");
  const navigate = useNavigate();
  const brands = usePartnerBrands();

  const columns: Column<PartnerBrandSummary>[] = [
    {
      key: "slug",
      header: "Slug",
      render: (b) => <span className="font-mono text-small">{b.slug}</span>,
    },
    {
      key: "displayName",
      header: "Display name",
      render: (b) => (
        <span className="inline-flex items-center gap-space-2">
          <span className="font-medium">{b.displayName}</span>
          {/* Inactive brands are invisible on the public funnel — a
              mid-wizard or abandoned draft is harmless, so flag it as such. */}
          {!b.isActive && <Badge status="review">Draft</Badge>}
        </span>
      ),
    },
    {
      key: "referenceCode",
      header: "Reference code",
      render: (b) => <span className="font-mono text-small">{b.referenceCode}</span>,
      mobileHidden: true,
    },
    {
      key: "owner",
      header: "Owner",
      render: (b) => <OwnerCell brand={b} />,
    },
    {
      key: "active",
      header: "Status",
      render: (b) =>
        b.isActive ? (
          <Badge status="finalized">Active</Badge>
        ) : (
          <Badge status="archived">Inactive</Badge>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-4 p-space-4">
      <PageHeader
        eyebrow="Partners"
        title="Partner Brands"
        subtitle="White-label skins for the public prequalification funnel — logo, colors, fonts and contact info per referral partner."
        actions={
          <Button onClick={() => navigate("/partners/brands/new")}>
            <Plus className="w-4 h-4 mr-space-1" aria-hidden />
            New partner brand
          </Button>
        }
      />

      <DataTable<PartnerBrandSummary>
        columns={columns}
        rows={brands.data ?? []}
        loading={brands.isLoading}
        error={brands.error ? extractErrorMessage(brands.error) : null}
        rowKey={(b) => b.id}
        emptyMessage="No partner brands yet. Create one to launch a co-branded funnel."
        onRowClick={(b) => navigate(`/partners/brands/${b.id}`)}
      />
    </div>
  );
}
